import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import { getClient, listClientUsers, updateUser } from "../../../api/chatbotApi";

export default function ChatbotClientUserEditPage() {
  const { clientId, userId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", business_role: "", role: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([getClient(clientId), listClientUsers(clientId)])
      .then(([clientResponse, usersResponse]) => {
        const users = Array.isArray(usersResponse)
          ? usersResponse
          : usersResponse?.users || usersResponse?.items || usersResponse?.data || [];
        const user = users.find((item) => String(item.id || item.user_id) === String(userId));
        if (!user) throw new Error("User not found");
        if (active) {
          setClient(clientResponse);
          setForm({
            name: user.name || user.full_name || user.username || "",
            email: user.email || user.email_address || "",
            business_role: user.business_role || "",
            role: Number(user.role ?? user.user_role ?? 1),
          });
        }
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.detail || err.message || "Failed to load user");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientId, userId]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await updateUser(userId, {
        name: form.name.trim(),
        email: form.email.trim(),
        business_role: form.business_role.trim(),
        role: Number(form.role),
      });
      navigate(`/clients/${clientId}/users`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/clients" className="inline-flex items-center gap-1 transition hover:text-slate-900"><ArrowLeft size={16} /> Clients</Link>
        <span>/</span>
        <Link to={`/clients/${clientId}/users`} className="transition hover:text-slate-900">{client?.name || "Client"} Users</Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">Edit User</span>
      </div>

      <form onSubmit={handleSave} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 border-b border-slate-100 pb-5">
          <h1 className="text-2xl font-bold text-slate-900">Edit User</h1>
          <p className="mt-1 text-sm text-slate-500">Update this user&apos;s profile and access role.</p>
        </div>
        {error && <div className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"><AlertCircle size={18} /><span>{error}</span></div>}
        {loading ? <div className="py-10 text-center text-sm text-slate-500">Loading user...</div> : (
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Name *</label>
              <input required value={form.name} onChange={updateField("name")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Email *</label>
              <input required type="email" value={form.email} onChange={updateField("email")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Business Role *</label>
              <input required value={form.business_role} onChange={updateField("business_role")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Role *</label>
              <select value={form.role} onChange={updateField("role")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none">
                <option value={0}>Super Admin</option>
                <option value={1}>Account Admin</option>
              </select>
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
          <Link to={`/clients/${clientId}/users`} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">Cancel</Link>
          <button type="submit" disabled={saving || loading} className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:opacity-50"><Save size={16} /> {saving ? "Saving..." : "Save Changes"}</button>
        </div>
      </form>
    </div>
  );
}
